import { useEffect, useRef, useState } from "react";
import { Eye, Loader2, Lock, MessagesSquare } from "lucide-react";
import api, { errorMessage } from "../../lib/api";
import {
  ACTION_STATES, ERROR_CODES, OUTCOMES, USAGE_MODELS, dateTimeOf, formatCost, formatDuration, formatTokens, timeOf, timeline, toolLabels,
} from "../../lib/assistantUsage";
import CopilotText from "./CopilotText";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";

const modelLabel = (value) => USAGE_MODELS.find((m) => m.value === value)?.label || value || "—";

export function OutcomeBadge({ outcome }) {
  const meta = OUTCOMES[outcome] || { label: outcome, tone: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-2xs font-medium ${meta.tone}`}>{meta.label}</span>;
}

// One question of the dashboard: every model call and every tool run, in order. The text of the exchange is never in this
// response — only the administrator can ask for it, one question at a time (journalled when it is someone else's).
export default function UsageTurnSheet({ turnId, isAdmin, currentUserId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState(null);
  const [text, setText] = useState(null);
  const [textState, setTextState] = useState("idle");
  const [conversation, setConversation] = useState(null);
  const [conversationState, setConversationState] = useState("idle");

  useEffect(() => {
    if (!turnId) return undefined;
    let cancelled = false;
    setDetail(null);
    setFailed(null);
    setText(null);
    setTextState("idle");
    setConversation(null);
    setConversationState("idle");
    api.get(`/assistant/usage/turns/${turnId}`)
      .then(({ data }) => { if (!cancelled) setDetail(data); })
      .catch((e) => { if (!cancelled) setFailed(errorMessage(e, "Impossible de charger cette question.")); });
    return () => { cancelled = true; };
  }, [turnId]);

  const showText = async () => {
    setTextState("loading");
    try {
      const { data } = await api.get(`/assistant/usage/turns/${turnId}/text`);
      setText(data);
      setTextState("shown");
    } catch (e) {
      setTextState(errorMessage(e, "Impossible d'afficher le texte de cet échange."));
    }
  };

  const showConversation = async () => {
    setConversationState("loading");
    try {
      const { data } = await api.get(`/assistant/usage/turns/${turnId}/conversation`);
      setConversation(data);
      setConversationState("shown");
    } catch (e) {
      setConversationState(errorMessage(e, "Impossible d'afficher cette conversation."));
    }
  };

  const steps = timeline(detail);
  const othersExchange = detail && detail.user_id !== currentUserId;

  return (
    <Sheet open={!!turnId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="usage-turn-sheet">
        <SheetHeader className="pr-8 text-left">
          <SheetTitle>Détail de la question</SheetTitle>
          <SheetDescription>{detail ? `${detail.user_name} · ${dateTimeOf(detail.at)} (UTC)` : "Chargement…"}</SheetDescription>
        </SheetHeader>

        {failed && <p className="mt-6 text-sm text-red-700">{failed}</p>}
        {!detail && !failed && (
          <div className="mt-6 space-y-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />)}</div>
        )}

        {detail && (
          <div className="mt-5 space-y-5 text-sm">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-slate-200 p-4 sm:grid-cols-3">
              <Figure label="Issue" value={<OutcomeBadge outcome={detail.outcome} />} />
              <Figure label="Modèle" value={modelLabel(detail.served_models?.[0] || detail.model)} />
              <Figure label="Coût estimé" value={formatCost(detail.cost_nanodollars)} />
              <Figure label="Appels modèle" value={detail.calls} />
              <Figure label="Tokens entrée / sortie" value={`${formatTokens(detail.input_tokens + detail.cache_read_input_tokens + detail.cache_creation_input_tokens)} / ${formatTokens(detail.output_tokens)}`} />
              <Figure label="Durée" value={formatDuration(detail.duration_ms)} />
            </dl>
            {detail.error_code && (
              <p className="rounded-lg bg-slate-50 px-3 py-2 text-slate-700">Raison : {ERROR_CODES[detail.error_code] || detail.error_code}</p>
            )}

            <section>
              <h4 className="mb-2 font-semibold text-slate-900">Chronologie</h4>
              <ol className="space-y-2" data-testid="usage-timeline">
                {steps.map((step, index) => <Step key={index} step={step} />)}
              </ol>
            </section>

            {detail.proposals?.length > 0 && (
              <section>
                <h4 className="mb-2 font-semibold text-slate-900">Actions proposées</h4>
                <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200" data-testid="usage-proposals">
                  {detail.proposals.map((p, i) => {
                    const state = ACTION_STATES[p.state] || ACTION_STATES.pending;
                    return (
                      <li key={i} className="flex items-center gap-3 px-3 py-2">
                        <span className="w-16 shrink-0 font-mono text-xs text-slate-400">{timeOf(p.at)}</span>
                        <span className={`min-w-0 flex-1 truncate ${p.danger ? "text-red-700" : "text-slate-800"}`}>{p.title}</span>
                        <span className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-2xs font-medium ${state.tone}`}>{state.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            <section className="rounded-xl border border-slate-200 p-4" data-testid="usage-text">
              <h4 className="mb-1 font-semibold text-slate-900">{conversationState === "shown" ? "Toute la conversation" : "Texte de l'échange"}</h4>
              {!detail.has_text && <p className="text-slate-500">Aucun texte : la question a été refusée avant d'être posée.</p>}
              {detail.has_text && !isAdmin && (
                <p className="flex items-center gap-2 text-slate-500"><Lock size={14} /> Réservé à l'administrateur.</p>
              )}
              {detail.has_text && isAdmin && !detail.text_available && (
                <p className="text-slate-500">Texte supprimé : durée de conservation dépassée ou conversation supprimée par la personne.</p>
              )}
              {detail.has_text && isAdmin && detail.text_available && textState !== "shown" && conversationState !== "shown" && (
                <div className="space-y-2">
                  <p className="text-slate-500">
                    {othersExchange ? "La consultation du texte d'un autre utilisateur est enregistrée dans le journal d'activité." : "Votre propre échange."}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={showText} disabled={textState === "loading"} data-testid="usage-show-text">
                      {textState === "loading" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Eye size={14} className="mr-1.5" />}
                      Afficher cet échange
                    </Button>
                    <ConversationButton state={conversationState} onClick={showConversation} />
                  </div>
                  {!["idle", "loading"].includes(textState) && <p className="text-red-700">{textState}</p>}
                </div>
              )}
              {textState === "shown" && text && conversationState !== "shown" && (
                <div className="mb-1 mt-2">
                  <ConversationButton state={conversationState} onClick={showConversation} />
                </div>
              )}
              {!["idle", "loading", "shown"].includes(conversationState) && <p className="mt-2 text-red-700">{conversationState}</p>}
              {conversationState === "shown" && conversation && <WholeConversation conversation={conversation} />}
              {textState === "shown" && text && conversationState !== "shown" && (
                <div className="mt-2 space-y-3">
                  <div>
                    <div className="text-xs font-medium text-slate-500">Question</div>
                    <p className="mt-1 whitespace-pre-wrap rounded-lg bg-slate-50 px-3 py-2 text-slate-800">{text.question}</p>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-500">Réponse</div>
                    <div className="mt-1 rounded-lg bg-slate-50 px-3 py-2 text-slate-800">
                      {text.answer ? <CopilotText text={text.answer} /> : <span className="text-slate-500">Aucune réponse.</span>}
                    </div>
                  </div>
                  {text.tool_calls?.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-slate-500">Arguments des outils</div>
                      <ul className="mt-1 space-y-1 font-mono text-xs text-slate-700">
                        {text.tool_calls.map((t, i) => <li key={i} className="break-all">{t.name} {JSON.stringify(t.input)}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Figure({ label, value }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-0.5 truncate font-medium tabular-nums text-slate-900">{value}</dd>
    </div>
  );
}

function Step({ step }) {
  if (step.kind === "question") {
    return <StepRow time={timeOf(step.at)} title="Question posée" />;
  }
  if (step.kind === "answer") {
    return <StepRow time="" title={step.outcome === "success" ? "Réponse envoyée" : `Fin : ${OUTCOMES[step.outcome]?.label || step.outcome}`} />;
  }
  if (step.kind === "tool") {
    const ok = step.outcome === "ok";
    return (
      <StepRow indent time={timeOf(step.at)} title={`Outil : ${step.label}`}
        detail={`${step.count != null ? `${step.count} résultat${step.count > 1 ? "s" : ""} · ` : ""}${formatDuration(step.duration_ms)}${ok ? "" : ` · ${step.outcome}`}`}
        tone={ok ? "" : "text-amber-800"} />
    );
  }
  const prompt = (step.input_tokens || 0) + (step.cache_read_input_tokens || 0) + (step.cache_creation_input_tokens || 0);
  return (
    <StepRow time={timeOf(step.at)} title={`Appel modèle n° ${step.round + 1}${step.tools?.length ? ` → ${toolLabels(step).join(", ")}` : ""}`}
      detail={`${formatTokens(prompt)} entrée (${formatTokens(step.cache_read_input_tokens || 0)} cache) · ${formatTokens(step.output_tokens || 0)} sortie · ${formatCost(step.cost_nanodollars)} · ${formatDuration(step.duration_ms)}`}
      tone={step.outcome === "error" ? "text-red-700" : ""} />
  );
}

function StepRow({ time, title, detail, indent = false, tone = "" }) {
  return (
    <li className={`flex gap-3 ${indent ? "pl-4" : ""}`}>
      <span className="w-16 shrink-0 pt-0.5 font-mono text-xs text-slate-400">{time}</span>
      <div className="min-w-0">
        <div className={`text-slate-800 ${tone}`}>{title}</div>
        {detail && <div className="text-xs text-slate-500">{detail}</div>}
      </div>
    </li>
  );
}

function ConversationButton({ state, onClick }) {
  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={state === "loading"} data-testid="usage-show-conversation">
      {state === "loading" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <MessagesSquare size={14} className="mr-1.5" />}
      Voir toute la conversation
    </Button>
  );
}

// Every exchange still kept, in order, the approval cards between them; the question of this row is outlined.
function WholeConversation({ conversation }) {
  const box = useRef(null);
  useEffect(() => {
    box.current?.querySelector("[data-current]")?.scrollIntoView({ block: "center" });
  }, [conversation]);
  const items = [
    ...conversation.messages.map((m) => ({ ...m, kind: "message" })),
    ...conversation.proposals.map((p) => ({ ...p, kind: "proposal" })),
  ].sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  return (
    <div ref={box} className="mt-3 space-y-2" data-testid="usage-conversation">
      {conversation.expired_exchanges > 0 && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
          {conversation.expired_exchanges} échange{conversation.expired_exchanges > 1 ? "s" : ""} plus ancien{conversation.expired_exchanges > 1 ? "s" : ""} :
          texte supprimé (durée de conservation dépassée).
        </p>
      )}
      {items.map((item, i) => {
        if (item.kind === "proposal") {
          const state = ACTION_STATES[item.state] || ACTION_STATES.pending;
          return (
            <div key={`p${i}`} className="flex items-center gap-2 pl-4 text-xs text-slate-500">
              <span>Carte proposée : {item.title}</span>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-2xs font-medium ${state.tone}`}>{state.label}</span>
            </div>
          );
        }
        const asked = item.role === "user";
        return (
          <div key={`m${i}`} data-current={item.current ? "true" : undefined}
            className={`rounded-lg px-3 py-2 ${asked ? "bg-slate-50" : "bg-white border border-slate-100"} ${item.current ? "ring-2 ring-jamora/40" : ""}`}>
            <div className="mb-0.5 flex items-center gap-2 text-xs text-slate-500">
              <span className="font-medium">{asked ? "Question" : "Réponse"}</span>
              <span className="font-mono">{timeOf(item.at)}</span>
              {item.current && asked && <span className="rounded-full bg-jamora/10 px-1.5 text-2xs font-medium text-jamora">Cette question</span>}
            </div>
            {asked ? <p className="whitespace-pre-wrap text-slate-800">{item.text}</p> : <div className="text-slate-800"><CopilotText text={item.text || ""} /></div>}
          </div>
        );
      })}
    </div>
  );
}
